import { z } from 'zod'
import {
  EnvironmentConfigurationError,
  SupabaseNotConfiguredError,
} from './errors/index'

/**
 * The single point of environment access. Nothing else in the codebase reads
 * `import.meta.env`, so a misconfigured deploy fails in one place with a
 * message naming the missing variable.
 *
 * Validation is LAZY. Supabase is not connected until milestone M4, so during
 * M1 to M3 the two Supabase variables are legitimately absent and the
 * application must still boot. See docs/ARCHITECTURE.md section 10.
 *
 * The one hard rule: a production build may not run in placeholder mode.
 * assertEnvironmentIsUsable() enforces that at startup.
 */

export const APP_ENVIRONMENTS = ['local', 'sandbox', 'production'] as const
export type AppEnvironment = (typeof APP_ENVIRONMENTS)[number]

export interface SupabaseEnv {
  readonly url: string
  readonly anonKey: string
}

const appEnvironmentSchema = z.enum(APP_ENVIRONMENTS)

const supabaseEnvSchema = z.object({
  url: z
    .string()
    .min(1)
    .refine((value) => URL.canParse(value), {
      message: 'must be a valid absolute URL, for example https://xxxx.supabase.co',
    }),
  // Length is a sanity check only. The key is public by design; the security
  // boundary is Row Level Security. See docs/SECURITY_AND_RLS.md.
  anonKey: z.string().min(20, 'looks too short to be a Supabase anon key'),
})

/** Reads a variable, treating empty and whitespace-only strings as absent. */
function readRaw(name: string): string | undefined {
  const source = import.meta.env as unknown as Record<string, unknown>
  const value = source[name]
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}

/**
 * Which environment this build points at. Drives the environment badge so
 * nobody confuses sandbox with production. Absent means 'local'.
 */
export function getAppEnvironment(): AppEnvironment {
  const raw = readRaw('VITE_APP_ENVIRONMENT') ?? 'local'
  const parsed = appEnvironmentSchema.safeParse(raw)
  if (!parsed.success) {
    throw new EnvironmentConfigurationError(
      `VITE_APP_ENVIRONMENT must be one of ${APP_ENVIRONMENTS.join(', ')}, received "${raw}".`,
      ['VITE_APP_ENVIRONMENT'],
    )
  }
  return parsed.data
}

/** True when both Supabase variables are present and well formed. */
export function isSupabaseConfigured(): boolean {
  const candidate = {
    url: readRaw('VITE_SUPABASE_URL'),
    anonKey: readRaw('VITE_SUPABASE_ANON_KEY'),
  }
  return supabaseEnvSchema.safeParse(candidate).success
}

/**
 * Supabase connection details, or a clear error naming what is missing.
 * Callers that can run without Supabase should check isSupabaseConfigured()
 * first rather than catching this.
 */
export function getSupabaseEnv(): SupabaseEnv {
  const url = readRaw('VITE_SUPABASE_URL')
  const anonKey = readRaw('VITE_SUPABASE_ANON_KEY')
  const parsed = supabaseEnvSchema.safeParse({ url, anonKey })

  if (!parsed.success) {
    const missing: string[] = []
    if (url === undefined) missing.push('VITE_SUPABASE_URL')
    if (anonKey === undefined) missing.push('VITE_SUPABASE_ANON_KEY')

    const detail =
      missing.length > 0
        ? `Missing: ${missing.join(', ')}.`
        : `Invalid: ${parsed.error.issues.map((issue) => `${issue.path.join('.')} ${issue.message}`).join('; ')}.`

    throw new SupabaseNotConfiguredError(
      `Supabase is not configured. ${detail} Copy .env.example to .env.local and fill in the values for your environment. Supabase is not connected until milestone M4.`,
      missing.length > 0
        ? missing
        : ['VITE_SUPABASE_URL', 'VITE_SUPABASE_ANON_KEY'],
    )
  }

  return parsed.data
}

/**
 * Placeholder mode: the application runs without a backend. Legitimate during
 * M1 to M3 and while a developer works on pure UI. Never legitimate in
 * production.
 */
export function isPlaceholderMode(): boolean {
  return !isSupabaseConfigured()
}

/**
 * Call once at startup. Fails loudly rather than letting a production build
 * silently serve a backend-less application.
 */
export function assertEnvironmentIsUsable(): void {
  const environment = getAppEnvironment()
  if (environment === 'production' && !isSupabaseConfigured()) {
    throw new EnvironmentConfigurationError(
      'VITE_APP_ENVIRONMENT is "production" but Supabase is not configured. A production build must never run in local placeholder mode.',
      ['VITE_SUPABASE_URL', 'VITE_SUPABASE_ANON_KEY'],
    )
  }
}
